import { create } from 'zustand';
import type {
  LoginResult,
  OnboardingStep,
  RegisterInput,
  UpdateProfileInput,
  UserProfile,
} from '@lizrd/contracts';
import * as api from '../lib/api';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: Status;
  user: UserProfile | null;
  /** Restore a session from the refresh cookie on app load. */
  bootstrap: () => Promise<void>;
  /** Step 1 of login. Signs in on success, or returns an MFA challenge for the
   * caller to complete via {@link completeMfaLogin} (Feature 25). */
  login: (email: string, password: string) => Promise<LoginResult>;
  /** Step 2 of login: redeem the MFA challenge + a code for a session. */
  completeMfaLogin: (challengeToken: string, code: string) => Promise<void>;
  /** Step 2 of login via a passkey: run the WebAuthn assertion for a session. */
  completePasskeyLogin: (challengeToken: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  /** DEV ONLY: create a fresh throwaway org in the ONBOARDING state and sign in,
   * for walking the onboarding wizard repeatedly without re-registering. */
  devStartOnboarding: () => Promise<void>;
  logout: () => Promise<void>;
  /** Sign out of all sessions on every device (AUTH-008). */
  logoutAll: () => Promise<void>;
  /** Redeem an emailed sandbox link (Feature 24): provision the sandbox + sign in. */
  completeSandbox: (token: string) => Promise<void>;
  /** Accept an invitation and sign in as the new user (INV-008). */
  acceptInvite: (token: string, name: string, password: string) => Promise<void>;
  /** Update the current user's own profile (USER-003,004). */
  updateProfile: (input: UpdateProfileInput) => Promise<void>;
  /** Advance guided onboarding past a completed step; updates the org's status
   * + step on the current user so the onboarding gate reacts immediately. */
  advanceOnboarding: (from: OnboardingStep) => Promise<void>;
  /** Step guided onboarding back to the previous visible step. */
  goBackOnboarding: (from: OnboardingStep) => Promise<void>;
  /** Re-fetch the session (fresh access token + profile) — e.g. right after the
   * user verifies their email, so `emailVerified` and the token both update. */
  reloadUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'loading',
  user: null,

  bootstrap: async () => {
    const result = await api.refreshSession();
    set(
      result ? { status: 'authenticated', user: result.user } : { status: 'anonymous', user: null },
    );
  },

  login: async (email, password) => {
    const result = await api.login({ email, password });
    // Only a full session flips us to authenticated; an MFA challenge is handed
    // back to the caller (LoginPage shows the code step) without a session.
    if ('accessToken' in result) {
      set({ status: 'authenticated', user: result.user });
    }
    return result;
  },

  completeMfaLogin: async (challengeToken, code) => {
    const result = await api.verifyMfaLogin(challengeToken, code);
    set({ status: 'authenticated', user: result.user });
  },

  completePasskeyLogin: async (challengeToken) => {
    const result = await api.passkeyLogin(challengeToken);
    set({ status: 'authenticated', user: result.user });
  },

  register: async (input) => {
    const result = await api.register(input);
    set({ status: 'authenticated', user: result.user });
  },

  devStartOnboarding: async () => {
    const result = await api.devStartOnboarding();
    set({ status: 'authenticated', user: result.user });
  },

  logout: async () => {
    await api.logout();
    set({ status: 'anonymous', user: null });
  },

  logoutAll: async () => {
    await api.logoutAll();
    set({ status: 'anonymous', user: null });
  },

  completeSandbox: async (token) => {
    const result = await api.verifySandboxLink(token);
    set({ status: 'authenticated', user: result.user });
  },

  acceptInvite: async (token, name, password) => {
    const result = await api.acceptInvitation(token, name, password);
    set({ status: 'authenticated', user: result.user });
  },

  updateProfile: async (input) => {
    const user = await api.updateProfile(input);
    set({ user });
  },

  reloadUser: async () => {
    const result = await api.refreshSession();
    set(
      result ? { status: 'authenticated', user: result.user } : { status: 'anonymous', user: null },
    );
  },

  advanceOnboarding: async (from) => {
    const state = await api.advanceOnboarding(from);
    set((s) =>
      s.user
        ? {
            user: {
              ...s.user,
              organizationStatus: state.status,
              onboardingStep: state.onboardingStep,
            },
          }
        : {},
    );
  },

  goBackOnboarding: async (from) => {
    const state = await api.goBackOnboarding(from);
    set((s) =>
      s.user
        ? {
            user: {
              ...s.user,
              organizationStatus: state.status,
              onboardingStep: state.onboardingStep,
            },
          }
        : {},
    );
  },
}));

/**
 * True when the signed-in tenant is a self-serve SANDBOX (demo). The sandbox is a
 * read-only-ish demo: Settings/Members/Billing and integration management are
 * hidden (and enforced 403 server-side). Recommendations stay interactive.
 */
export const useIsSandbox = (): boolean =>
  useAuthStore((s) => s.user?.organizationKind === 'SANDBOX');
